import { KIND } from './kinds.js';
import { signEvent, checkEvent } from './event.js';
import { normalizePubkey } from './keys.js';
import { tagValue } from './util.js';

/**
 * Sender manifest -- Herald's answer to "the marketing tool should not hold the
 * company's root key".
 *
 * The root key is cold. It signs exactly one thing: a manifest listing the
 * subkeys allowed to send, each with a validity window and a scope. Everything
 * a subscriber sees is verified back to the root key that was in the QR code,
 * so rotating or revoking a compromised sending key never invalidates the
 * public identifier customers have already scanned.
 */

export const MANIFEST_D_TAG = 'herald-manifest';

/**
 * @param {{domain?:string, relays?:string[],
 *          keys?:Array<{pubkey:string,label?:string,scopes?:string[],notBefore?:number,notAfter?:number}>,
 *          revoked?:Array<{pubkey:string,at:number,reason?:string}>}} manifest
 * @param {Uint8Array|string} rootSecretKey
 */
export function buildManifest(manifest, rootSecretKey) {
  const content = JSON.stringify({
    domain: manifest.domain ?? null,
    relays: manifest.relays ?? [],
    keys: (manifest.keys ?? []).map((k) => ({
      pubkey: normalizePubkey(k.pubkey),
      label: k.label ?? '',
      scopes: k.scopes ?? ['*'],
      not_before: k.notBefore ?? 0,
      not_after: k.notAfter ?? 0, // 0 = no expiry
    })),
    revoked: (manifest.revoked ?? []).map((r) => ({
      pubkey: normalizePubkey(r.pubkey),
      at: r.at,
      reason: r.reason ?? '',
    })),
  });
  const tags = [['d', MANIFEST_D_TAG]];
  if (manifest.domain) tags.push(['domain', manifest.domain]);
  for (const relay of manifest.relays ?? []) tags.push(['relay', relay]);
  return signEvent({ kind: KIND.MANIFEST, content, tags, created_at: manifest.createdAt }, rootSecretKey);
}

export function parseManifest(event) {
  if (event.kind !== KIND.MANIFEST) throw new Error(`not a manifest (kind ${event.kind})`);
  if (tagValue(event, 'd') !== MANIFEST_D_TAG) throw new Error('manifest has wrong d tag');
  const body = JSON.parse(event.content);
  return {
    root: event.pubkey,
    domain: body.domain ?? null,
    relays: body.relays ?? [],
    keys: body.keys ?? [],
    revoked: body.revoked ?? [],
    updatedAt: event.created_at,
  };
}

/**
 * Revocation semantics, stated once so that implementations agree:
 *
 *   in `keys` with `not_after`      -- retired; messages inside the window verify
 *   in `keys` AND in `revoked` at T -- compromised at T; messages before T verify
 *   in `revoked` only               -- disowned; nothing it ever signed verifies
 *   absent from both                -- never authorised
 *
 * Retiring a key is the common case and must not break history: a subscriber
 * has to stay able to verify the newsletter they received last month.
 */

/** Does a granted scope cover a required one? "broadcast:*" covers "broadcast:news". */
export function scopeCovers(granted, required) {
  if (granted === '*') return true;
  if (granted === required) return true;
  const [gArea, gName] = granted.split(':');
  const [rArea, rName] = required.split(':');
  return gArea === rArea && gName === '*' && rName !== undefined;
}

/**
 * The core trust decision: was this event signed by a key the root authorised
 * for this purpose, at the time it claims to have been created?
 *
 * @param {object} event the event to authorise
 * @param {string} rootPubkey the identifier the subscriber actually scanned
 * @param {object|null} manifestEvent signed manifest event, or null if none seen
 * @param {string} requiredScope e.g. "broadcast:news" or "transactional:invoices"
 * @returns {{ok:true, via:'root'|'delegated', key?:object}|{ok:false, error:string}}
 */
export function verifySenderChain(event, rootPubkey, manifestEvent, requiredScope) {
  const root = normalizePubkey(rootPubkey);

  if (event.pubkey === root) return { ok: true, via: 'root' };

  if (!manifestEvent) {
    return { ok: false, error: 'signed by a non-root key but no manifest available to authorise it' };
  }
  const manifestCheck = checkEvent(manifestEvent);
  if (!manifestCheck.ok) return { ok: false, error: `manifest invalid: ${manifestCheck.error}` };
  if (manifestEvent.pubkey !== root) {
    return { ok: false, error: 'manifest is not signed by the root key' };
  }

  let manifest;
  try {
    manifest = parseManifest(manifestEvent);
  } catch (err) {
    return { ok: false, error: `manifest unparseable: ${err.message}` };
  }

  const revocation = manifest.revoked.find((r) => r.pubkey === event.pubkey);
  if (revocation && event.created_at >= revocation.at) {
    // `at: 0` is the root disowning everything the key ever signed, which is a
    // stronger statement than "compromised from this moment".
    return {
      ok: false,
      error: revocation.at === 0
        ? 'signing key was revoked with its entire history disowned by the root'
        : `signing key was revoked by the root at ${revocation.at}`,
    };
  }

  const key = manifest.keys.find((k) => k.pubkey === event.pubkey);
  if (!key) {
    // Three distinct states, and the difference is load-bearing:
    //   listed in `keys` and in `revoked` at T -> valid before T, invalid after
    //   listed only in `revoked`               -> history disowned entirely
    //   listed nowhere                         -> never authorised
    return revocation
      ? { ok: false, error: 'signing key was revoked and its entire history disowned by the root' }
      : { ok: false, error: 'signing key is not listed in the sender manifest' };
  }
  if (key.not_before && event.created_at < key.not_before) {
    return { ok: false, error: 'signing key was not yet valid when the message was created' };
  }
  if (key.not_after && event.created_at > key.not_after) {
    return { ok: false, error: 'signing key had expired when the message was created' };
  }
  if (requiredScope && !key.scopes.some((s) => scopeCovers(s, requiredScope))) {
    return { ok: false, error: `signing key lacks scope "${requiredScope}" (has: ${key.scopes.join(', ')})` };
  }
  return { ok: true, via: 'delegated', key };
}
